// src/pages/StaffAffidavitDCI.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, mapRows } from "../utils/api";
import { useAuth } from "../context/AuthContext";
import { useDarkMode, DM } from "../context/DarkModeContext";
import DashboardHeader from "../components/DashboardHeader";

import { useReactToPrint } from "react-to-print";
import { downloadPDF, formatDuration, fetchDoctorFilter } from "../utils/reportUtils";

export default function StaffAffidavitDCI() {
  const navigate = useNavigate();
  const { logout, userRole, currentUser } = useAuth();
  const { darkMode } = useDarkMode();
  const d = darkMode;
  const printRef = useRef();
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  const [filterDept, setFilterDept] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState("");
  
  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [usersRes, isDoctorUser, dRes] = await Promise.all([
        api.get("/users"),
        fetchDoctorFilter(),
        api.get("/departments?orderBy=name"),
      ]);

      const allUsers = mapRows(usersRes);
      const loggedInUser = allUsers.find(u => u.id === currentUser?.uid);
      const isFacultyOnly = loggedInUser?.role === "faculty";
      const facultyDept = loggedInUser?.department;

      let list = allUsers
        .filter(u =>
          (u.role === "faculty" || u.role === "admin") &&
          u.status !== "inactive" &&
          isDoctorUser(u)
        )
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

      if (isFacultyOnly && facultyDept) {
        list = list.filter(u => u.department === facultyDept);
        setDepartments([facultyDept]);
        setFilterDept(facultyDept);
      } else {
        setDepartments(mapRows(dRes).map((d) => d.name).filter(Boolean));
      }

      setStaffList(list);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  const handleLogout = async () => {
    await logout();
    navigate(userRole === "admin" || userRole === "superadmin" ? "/admin" : "/login");
  };

  const filteredStaff = staffList.filter(s => !filterDept || (s.department || "").toLowerCase().trim() === filterDept.toLowerCase().trim());
  const selectedStaff = staffList.find(s => s.id === selectedStaffId);

  // Data Extractors
  const extractVal = (staff, keywords) => {
    if (!staff) return "";
    for (const k of Object.keys(staff)) {
      const kl = k.toLowerCase();
      if (keywords.some(kw => kl.includes(kw)) && typeof staff[k] === 'string' && staff[k].trim() !== "") {
        return staff[k];
      }
    }
    return "";
  };

  const getDOB = (staff) => extractVal(staff, ["dob", "birth"]);
  const getPan = (staff) => extractVal(staff, ["pan", "p.a.n"]);
  const getFatherName = (staff) => extractVal(staff, ["father", "husband", "parent", "guardian"]);
  const getAadhar = (staff) => extractVal(staff, ["aadhar", "aadhaar", "uidai", "adhar"]);
  const getVoter = (staff) => extractVal(staff, ["voter", "epic"]);
  const getPassport = (staff) => extractVal(staff, ["passport"]);
  const getPhone = (staff) => extractVal(staff, ["mobile", "phone", "contact"]);
  
  const getFullAddress = (staff, type) => {
    if (!staff) return "";
    const addr = type === "present" 
      ? extractVal(staff, ["present_address", "current_address", "communication_address", "residential_address"]) 
      : extractVal(staff, ["permanent_address", "perm_address"]);
    
    if (!addr) {
      // If no specific present/permanent found, try generic address for present
      if (type === "present") {
        const generic = extractVal(staff, ["address"]);
        if (generic) return generic;
      }
      return "";
    }

    // Try to append district and state if they are separate fields and not already in addr
    const city = extractVal(staff, ["district", "city"]);
    const state = extractVal(staff, ["state"]);
    const pincode = extractVal(staff, ["pincode", "pin_code", "zip"]);
    
    let full = addr;
    if (city && !full.toLowerCase().includes(city.toLowerCase())) full += ", " + city;
    if (state && !full.toLowerCase().includes(state.toLowerCase())) full += ", " + state;
    if (pincode && !full.toLowerCase().includes(pincode.toLowerCase())) full += " - " + pincode;
    
    return full;
  };
  
  // Custom Date Formatter
  const formatDOB = (dobStr) => {
    if (!dobStr) return "DD / MM / YYYY";
    // Check if it's already formatted well enough, else parse
    if (dobStr.includes("-")) {
      const parts = dobStr.split("-");
      if (parts[0].length === 4) return `${parts[2]} / ${parts[1]} / ${parts[0]}`; // YYYY-MM-DD to DD / MM / YYYY
    }
    return dobStr;
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `DCI_Affidavit_${selectedStaff?.name || ""}`,
  });

  const handleDownloadPDF = async () => {
    setIsProcessing(true);
    await downloadPDF(printRef, `DCI_Affidavit_${selectedStaff?.name || ""}.pdf`, { orientation: "p" });
    setIsProcessing(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'Times New Roman', Times, serif" }}>
      {/* 1. Header & Controls (Hidden in Print) */}
      <div className="no-print">
        <DashboardHeader label="Staff Affidavit DCI">
          <button onClick={() => navigate(-1)} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 18px", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>← Back</button>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleDownloadPDF} disabled={!selectedStaff || isProcessing} style={{ background: "#7c3aed", color: "#fff", border: "none", padding: "8px 18px", borderRadius: 8, cursor: selectedStaff && !isProcessing ? "pointer" : "not-allowed", fontWeight: 700, fontSize: 15 }}>
              {isProcessing ? "⏳..." : "📄 Download PDF"}
            </button>
            <button onClick={() => handlePrint()} disabled={!selectedStaff} style={{ background: "#2563eb", color: "#fff", border: "none", padding: "8px 18px", borderRadius: 8, cursor: selectedStaff ? "pointer" : "not-allowed", fontWeight: 700, fontSize: 15 }}>
              🖨️ Print Affidavit
            </button>
          </div>
          <button onClick={handleLogout} style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 15 }}>Logout</button>
        </DashboardHeader>

        <div style={{ padding: "24px 32px", background: "#fff", borderBottom: "1px solid #e2e8f0", display: "flex", gap: 24, alignItems: "center", fontFamily: "sans-serif" }}>
          <div>
            <label style={{ display: "block", fontSize: 14, fontWeight: 700, color: "#64748b", marginBottom: 6 }}>Department</label>
            <select value={filterDept} onChange={e => { setFilterDept(e.target.value); setSelectedStaffId(""); }} style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #cbd5e1", minWidth: 250 }}>
              <option value="">All Departments</option>
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 14, fontWeight: 700, color: "#64748b", marginBottom: 6 }}>Select Faculty</label>
            <select value={selectedStaffId} onChange={e => setSelectedStaffId(e.target.value)} style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid #cbd5e1", minWidth: 350 }}>
              <option value="">-- Choose Faculty Member --</option>
              {filteredStaff.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.empId || 'No ID'})</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading && <div className="no-print" style={{ padding: 40, textAlign: "center", color: "#64748b", fontFamily: "sans-serif" }}>Loading data...</div>}
      
      {!selectedStaff && !loading && (
        <div className="no-print" style={{ padding: 60, textAlign: "center", color: "#94a3b8", fontFamily: "sans-serif" }}>
          <h2>📄</h2>
          <p>Please select a faculty member from the dropdown above to generate their DCI Affidavit.</p>
        </div>
      )}

      {/* 2. Print Container */}
       {selectedStaff && (
         <div ref={printRef} className="print-container" style={{ padding: "40px", display: "flex", flexDirection: "column", gap: 30, alignItems: "center" }}>
          
          <style>{`
            @media print {
              @page { size: A4 portrait; margin: 15mm 20mm; }
              body { margin: 0; background: #fff !important; }
              .no-print { display: none !important; }
              .print-container { padding: 0 !important; gap: 0 !important; }
              .page-break { page-break-after: always; clear: both; }
              .affidavit-page { box-shadow: none !important; border: none !important; width: 100% !important; min-height: 0 !important; padding: 0 !important; margin: 0 !important; }
            }
            .affidavit-page {
              background: #fff;
              width: 210mm;
              min-height: 297mm;
              box-shadow: 0 4px 16px rgba(0,0,0,0.1);
              padding: 20mm;
              box-sizing: border-box;
              font-family: 'Times New Roman', serif;
              color: #000;
              line-height: 1.5;
            }
            .a-table { width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 13px; }
            .a-table th, .a-table td { border: 1px solid #000; padding: 6px 10px; text-align: left; }
            .a-table th { font-weight: bold; text-align: center; }
            .a-sec { margin-bottom: 25px; }
            .a-title { text-align: center; font-weight: bold; margin-bottom: 30px; font-size: 16px; }
            .a-right { text-align: right; }
            .a-flex { display: flex; justify-content: space-between; margin-top: 60px; }
            .a-list { padding-left: 20px; list-style-type: none; margin: 0; }
            .a-list li { margin-bottom: 8px; display: flex; }
            .a-list li span:first-child { width: 120px; flex-shrink: 0; }
          `}</style>

          {/* PAGE 1 */}
          <div className="affidavit-page page-break" style={{ paddingTop: "120mm" }}>
            <div className="a-right" style={{ marginBottom: 20 }}>(Appendix -1)</div>
            <div className="a-title">AFFIDAVIT</div>
            <div style={{ borderTop: "1px solid #ccc", margin: "-10px 0 30px" }}></div>

            <div className="a-sec">
              1. I, <strong>{selectedStaff?.title || 'Dr.'} {selectedStaff?.name}</strong> D/o / S/o <strong>{getFatherName(selectedStaff) || "______________________________"}</strong>
            </div>
            
            <div className="a-sec">
              2. Date of Birth (DD/MM/YYYY) : <strong>{formatDOB(getDOB(selectedStaff)).replace(/\s/g, '').split('').join(' ')}</strong>
            </div>
            
            <div className="a-sec">
              3. Residential Address of Faculty
              <div style={{ marginLeft: 30, marginTop: 10 }}>
                <div style={{ display: "flex", marginBottom: 15 }}>
                  <span style={{ width: 100 }}>a. Present :</span>
                  <div style={{ flex: 1 }}><strong>{getFullAddress(selectedStaff, "present") || "______________________________________________________"}</strong></div>
                </div>
                <div style={{ display: "flex" }}>
                  <span style={{ width: 100 }}>b. Permanent :</span>
                  <div style={{ flex: 1 }}><strong>{getFullAddress(selectedStaff, "permanent") || "______________________________________________________"}</strong></div>
                </div>
              </div>
            </div>

            <div className="a-sec">
              4. Contact Details:
              <div style={{ marginLeft: 30, marginTop: 10 }}>
                <table style={{ border: "none", width: "100%", lineHeight: 2 }}>
                  <tbody>
                    <tr><td style={{ width: 220 }}>Mobile No.:</td><td><strong>{getPhone(selectedStaff) || "________________"}</strong></td></tr>
                    <tr><td>Resi. Tel. No. with STD Code:</td><td><strong>________________</strong></td></tr>
                    <tr><td>Email:</td><td><strong>{selectedStaff?.email || "________________"}</strong></td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="a-flex" style={{ marginTop: 150 }}>
              <div>(Signature of Faculty)</div>
              <div>(Signature of Dean/Principal)</div>
            </div>
            <div className="a-right" style={{ marginTop: 30, fontSize: 15 }}>Contd/.....2</div>
          </div>

          {/* PAGE 2 */}
          <div className="affidavit-page page-break">
            <div className="a-title">--2--</div>

            <div className="a-sec">
              5. Any one documents from 5a and 5b is mandatory:
              <table className="a-table" style={{ marginTop: 15 }}>
                <thead>
                  <tr>
                    <th>5a</th><th>Proof of Photo ID</th><th>Document No.</th>
                    <th>5b</th><th>Proof of Residence</th><th>Document No.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{textAlign: 'center'}}>1</td><td>Passport</td><td style={{textAlign: 'center', fontWeight:'bold'}}>{getPassport(selectedStaff) || "-"}</td>
                    <td style={{textAlign: 'center'}}>1</td><td>Passport</td><td style={{textAlign: 'center', fontWeight:'bold'}}>{getPassport(selectedStaff) || "-"}</td>
                  </tr>
                  <tr>
                    <td style={{textAlign: 'center'}}>2</td><td>Voter ID Card</td><td style={{textAlign: 'center', fontWeight:'bold'}}>{getVoter(selectedStaff) || "-"}</td>
                    <td style={{textAlign: 'center'}}>2</td><td>Aadhaar Card</td><td style={{textAlign: 'center', fontWeight:'bold'}}>{getAadhar(selectedStaff) || "-"}</td>
                  </tr>
                  <tr>
                    <td style={{textAlign: 'center'}}>3</td><td>Driving License</td><td style={{textAlign: 'center', fontWeight:'bold'}}>-</td>
                    <td style={{textAlign: 'center'}}>3</td><td>Voter ID Card</td><td style={{textAlign: 'center', fontWeight:'bold'}}>{getVoter(selectedStaff) || "-"}</td>
                  </tr>
                  <tr>
                    <td style={{textAlign: 'center'}}>4</td><td>Aadhaar Card</td><td style={{textAlign: 'center', fontWeight:'bold'}}>{getAadhar(selectedStaff) || "-"}</td>
                    <td style={{textAlign: 'center'}}>4</td><td>Bill - Electricity / Landline Phone</td><td style={{textAlign: 'center', fontWeight:'bold'}}>-</td>
                  </tr>
                  <tr>
                    <td style={{textAlign: 'center'}}>5</td><td>-</td><td style={{textAlign: 'center'}}>-</td>
                    <td style={{textAlign: 'center'}}>5</td><td>Regd. Rent Agreement</td><td style={{textAlign: 'center'}}>-</td>
                  </tr>
                </tbody>
              </table>
              <div style={{ fontSize: 13, textAlign: "center" }}>Note:- Original Documents are mandatory for verification. All Documents/Certified Translation, must be in English.</div>
            </div>

            <div className="a-sec" style={{ marginTop: 40, lineHeight: 2 }}>
              6. Pan Card No. <strong>{getPan(selectedStaff) || "_____________________"}</strong> <span style={{ fontSize: 14, marginLeft: 20 }}>Certified copy to be enclosed.</span><br/>
              7. Aadhaar Card No. <strong>{getAadhar(selectedStaff) || "_____________________"}</strong> <span style={{ fontSize: 14, marginLeft: 20 }}>Certified copy to be enclosed.</span>
            </div>
            
            <div style={{ position: "relative", height: 200 }}>
              {/* Spacer mimicking the original scan */}
            </div>
            <div className="a-right" style={{ marginTop: 30, fontSize: 15 }}>Contd/.....3</div>
          </div>

          {/* PAGE 3 */}
          <div className="affidavit-page page-break">
            <div className="a-title">--3--</div>

            <div className="a-sec">
              8. QUALIFICATIONS:
              <table className="a-table">
                <thead>
                  <tr>
                    <th>Degree</th><th>College of Study</th><th>University</th><th>Year & Month of Passing</th>
                    <th>Speciality</th><th>Name of the State Dental Council</th><th>Reg. No. of UG & PG with date</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedStaff?.educationalQualifications || []).filter(q => q.degree && q.course).length > 0 ? (
                    selectedStaff.educationalQualifications.map((q, i) => (
                      <tr key={i}>
                        <td style={{textAlign: 'center'}}>{q.degree}</td>
                        <td>{q.institution}</td>
                        <td>{q.board}</td>
                        <td style={{textAlign: 'center'}}>{q.yop}</td>
                        <td style={{textAlign: 'center'}}>{q.major || q.specialization || "-"}</td>
                        <td style={{textAlign: 'center'}}>{q.stateCouncil || extractVal(selectedStaff, ["council"])}</td>
                        <td style={{textAlign: 'center'}}>{q.regNo ? `${q.regNo}${q.regDate ? ` (${q.regDate})` : ''}` : extractVal(selectedStaff, ["dci", "reg", "registration"])}</td>
                      </tr>
                    ))
                  ) : (
                    <>
                      <tr>
                        <td style={{textAlign: 'center'}}>BDS</td><td></td><td></td><td></td><td></td><td></td><td></td>
                      </tr>
                      <tr>
                        <td style={{textAlign: 'center'}}>MDS</td><td></td><td></td><td></td><td></td><td></td><td></td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
              <div style={{ fontSize: 13 }}>*Enclosed certified copy of the State Council Registration renewed till date.</div>
            </div>

            <div className="a-sec" style={{ marginTop: 30 }}>
              9. Present Designation : <strong>{selectedStaff?.designation || "__________________"}</strong>
            </div>

            <div className="a-sec" style={{ textAlign: "center", fontWeight: "bold", textTransform: "uppercase" }}>
              ADHIPARASAKTHI DENTAL COLLEGE AND HOSPITAL, <br/>
              MELMARUVATHUR - 603319.
            </div>

            <div className="a-sec">
              10. Name and Postal Address of College/Institution: <strong>ADHIPARASAKTHI DENTAL COLLEGE AND HOSPITAL, MELMARUVATHUR - 603319.</strong>
            </div>

            <div className="a-sec">
              11. Present Institute Appointment Order No.: <strong>________________________</strong>, Date : <strong>________________</strong>
            </div>

            <div className="a-sec">
              12. Before joining present institution I was working at as and relieved on after resigning.
              <ul style={{ listStyleType: "none", margin: "10px 0 0 20px" }}>
                <li>a. Appointment Order No. : of the previous appointment.</li>
                <li>b. Relieving Order No. : </li>
              </ul>
            </div>

            <div className="a-sec" style={{ marginTop: 30 }}>
              13. TEACHING EXPERIENCE:
              <table className="a-table">
                <thead>
                  <tr>
                    <th>Position</th><th>Name of Institution</th><th>From</th><th>To</th><th>Total Experience Year-Month-Days</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedStaff?.workExperiences || []).length > 0 ? (
                    selectedStaff.workExperiences.map((w, i) => (
                      <tr key={i}>
                        <td>{w.jobTitle || w.designation}</td>
                        <td>{w.name || w.institution}</td>
                        <td style={{textAlign: 'center'}}>{formatDOB(w.fromDate)}</td>
                        <td style={{textAlign: 'center'}}>{formatDOB(w.toDate) || "Till Date"}</td>
                        <td style={{textAlign: 'center'}}>{formatDuration(w.totalExp)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td style={{textAlign: 'center'}}>{selectedStaff?.designation || "-"}</td>
                      <td>Adhiparasakthi Dental College & Hospital</td>
                      <td></td>
                      <td style={{textAlign: 'center'}}>Till Date</td>
                      <td></td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan="4" style={{ textAlign: "right", fontWeight: "bold", paddingRight: 20 }}>Total Experience</td>
                    <td style={{textAlign: 'center', fontWeight: "bold"}}>
                      {/* Summation logic can go here if needed, otherwise grab from a core field */}
                      {formatDuration(extractVal(selectedStaff, ["total_experience", "experience"]))}
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{ fontSize: 13 }}>* Less than one year teaching experience will not be considered. * Use separate box for each Institution.</div>
            </div>

            <div className="a-flex" style={{ marginTop: 60 }}>
              <div>(Signature of Faculty)</div>
              <div>(Signature of Dean/Principal)</div>
            </div>
            <div className="a-right" style={{ marginTop: 30, fontSize: 15 }}>Contd/.....4</div>
          </div>

          {/* PAGE 4 */}
          <div className="affidavit-page page-break">
            <div className="a-title">--4--</div>

            <div className="a-sec">
              14. TOTAL SALARY DRAWN FROM THE COLLEGE IN THE LAST SIX(6) MONTHS:(List enclosed)
              <table className="a-table">
                <thead>
                  <tr>
                    <th>S.No</th><th>Month</th><th>Amount Received</th><th>Tax Deducted</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5, 6].map(n => (
                    <tr key={n}>
                      <td style={{textAlign: 'center'}}>{n}</td><td></td><td></td><td></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 13 }}>*(Last Six(6) months - Certified Copy of Bank Statement/Pass Book by the bank must be attached)</div>
            </div>

            <div className="a-sec" style={{ marginTop: 30 }}>
              15. TDS FOR THE LAST THREE FINANCIAL YEARS:
              <table className="a-table">
                <thead>
                  <tr>
                    <th>S.No</th><th>Financial Year</th><th>Total Tax Deducted Yearly</th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3].map(n => (
                    <tr key={n}>
                      <td style={{textAlign: 'center'}}>{n}</td><td></td><td></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 13 }}>*(Copy of Form 16 generated from TRACES for last three financial years to be attached)</div>
            </div>

            <div className="a-sec" style={{ marginTop: 30 }}>
              16. DETAILS OF PUBLICATIONS:
              <table className="a-table">
                <thead>
                  <tr>
                    <th style={{width: '50px'}}>S.No</th><th>Title of the Articles</th><th>Journal Details</th><th>Points</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const affidavitPubs = (selectedStaff?.articlePublications || []).filter(p => p.affidavit);
                    return affidavitPubs.length > 0 ? (
                      affidavitPubs.map((p, i) => (
                        <tr key={i}>
                          <td style={{textAlign: 'center'}}>{i+1}</td>
                          <td>{p.titleOfArticle || p.title}</td>
                          <td>{p.journalDetails || p.journal}</td>
                          <td style={{textAlign: 'center'}}>{p.points || 15}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td style={{textAlign: 'center'}}>1</td><td></td><td></td><td></td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
              <div style={{ fontSize: 13, textAlign: "justify", marginTop: 10 }}>
                Note: Submit certified clear Photocopies of all the documents mentioned in Serial No. 5, 6, 7, 8, 11, 12, 14 & 15 alongwith the Affidavit. Serial No. 13 & 16 to be submitted separately. All copies must be signed by the faculty member and counter signed by the Principal/Dean with date.
              </div>
            </div>

            <div className="a-flex" style={{ marginTop: 60 }}>
              <div>(Signature of Faculty)</div>
              <div>(Signature of Dean/Principal)</div>
            </div>
            <div className="a-right" style={{ marginTop: 30, fontSize: 15 }}>Contd/.....5</div>
          </div>

          {/* PAGE 5 */}
          <div className="affidavit-page page-break" style={{ pageBreakAfter: "auto" }}>
            <div className="a-title">--5--</div>
            <div className="a-title">DECLARATION</div>

            <div className="a-sec" style={{ textAlign: "justify", lineHeight: 1.8 }}>
              <table style={{width: "100%", borderSpacing: "0 10px"}}>
                <tbody>
                  <tr>
                    <td style={{verticalAlign: "top", width: "30px"}}>1</td>
                    <td>I, <strong>{selectedStaff?.title || 'Dr.'} {selectedStaff?.name}</strong> do hereby give an undertaking that I am working as a full time salaried employee (as per UGC Norms) designated as <strong>{selectedStaff?.designation || "_______________"}</strong> in the Department of <strong>{selectedStaff?.department || "_______________"}</strong> at <strong>Adhiparasakthi Dental College and Hospital</strong> on all working days, working Hours from 08:30 am to 03:30 pm.</td>
                  </tr>
                  <tr>
                    <td style={{verticalAlign: "top"}}>2</td>
                    <td>I am working as a Full Time/Part Time* faculty.<br/>(*As per Rule 16 of DCI, Master of Dental Surgery Course Regulations, 2017)</td>
                  </tr>
                  <tr>
                    <td style={{verticalAlign: "top"}}>3</td>
                    <td>I have not presented myself to any other Institution as a faculty in the current academic year for the purpose of DCI Inspection.</td>
                  </tr>
                  <tr>
                    <td style={{verticalAlign: "top"}}>4</td>
                    <td>I am not having private practice anywhere.</td>
                  </tr>
                  <tr>
                    <td style={{verticalAlign: "top"}}>5</td>
                    <td>I, hereby, declare that the above information and documents provided by me are absolutely true, correct and authentic to the best of my knowledge. In the event of any statement made in this declaration is found to be incorrect or false. I fully understand that I am liable for any necessary disciplinary/legal action.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="a-flex" style={{ marginTop: 50 }}>
              <div>Date:</div>
              <div>Signature of the Depondent</div>
            </div>

            <div className="a-sec" style={{ marginTop: 40, textAlign: "justify", lineHeight: 1.6 }}>
              This is to certify that the information given by the above deponent is correct and nothing has been concealed and deponent is working in the <strong>{selectedStaff?.department || "_______________"}</strong> as <strong>{selectedStaff?.designation || "_______________"}</strong> as a full-time teacher in our college and is not engaged in full-time private practice anywhere.
            </div>

            <div className="a-flex" style={{ marginTop: 70 }}>
              <div style={{ textAlign: "center" }}>
                ___________________________________<br/>
                Signature of the Principal of the College<br/>
                <span style={{ fontSize: 15 }}>with seal and date</span>
              </div>
              <div style={{ textAlign: "center" }}>
                ___________________________________<br/>
                Signature of the Chairman of the Trust<br/>
                <span style={{ fontSize: 15 }}>with seal and date</span>
              </div>
            </div>
            
            <div style={{ borderTop: "1px dashed #ccc", margin: "30px 0" }}></div>

            <div className="a-sec" style={{ textAlign: "center", fontWeight: "bold" }}>
              Attestation by Notary Public/Oath Commissioner<br/><br/>
              CERTIFIED THAT THE DEPONENT
            </div>

            <div className="a-sec" style={{ textAlign: "justify", lineHeight: 1.8 }}>
              I, <strong>{selectedStaff?.title || 'Dr.'} {selectedStaff?.name}</strong> Identified by ________________________ has solemnly affirmed before me at ________________________ on at Sl.No.______ that the contents of the affidavit which have been read and explained to him/her are true and correct to his/her knowledge.
            </div>

            <div className="a-sec" style={{ textAlign: "center", fontWeight: "bold", marginTop: 50 }}>
              Signature Notary Public/Oath Commissioner
            </div>

            <div style={{ borderTop: "1px dashed #ccc", margin: "30px 0" }}></div>

            <div className="a-sec" style={{ textAlign: "center", fontWeight: "bold" }}>
              Counter Signature of the Deponent<br/>
              <span style={{ fontSize: 15, fontWeight: "normal" }}>(On the day of Inspection)</span>
            </div>

            <div className="a-sec" style={{ textAlign: "justify", lineHeight: 1.8 }}>
              We have verified all the relevant documents and confirmed that information given is true to our knowledge and the above staff member was present during the inspection.
            </div>

             <div className="a-flex" style={{ marginTop: 50 }}>
              <div style={{ flex: 1 }}>
                (Signature of Inspector - 1)<br/>
                Dr. ________________________________________<br/>
                Date _______________________
              </div>
              <div style={{ flex: 1, textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <span>(Signature of Inspector - 2)</span>
                <span style={{ width: 250, textAlign: "left" }}>Dr. __________________________________</span>
                <span style={{ width: 250, textAlign: "left" }}>Date ______________________</span>
              </div>
            </div>

            <div className="a-sec" style={{ marginTop: 40, fontSize: 13 }}>
              [N.B. Please note that making false statement in the affidavit will attract the relevant provisions of the Indian Penal Code etc.]
            </div>

          </div>

        </div>
      )}
    </div>
  );
}
